import clsx from 'clsx';
import { ReactNode, useCallback, useRef, useState } from 'react';
import { Autoplay, Pagination, type Swiper as SwiperInstance, type SwiperOptions } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/autoplay';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/pagination';

import styles from './Slider.module.scss';

type SliderProps = {
  items: Array<{
    id?: string | number;
    content: ReactNode;
  }>;
  spaceBetween?: number;
  slidesPerView?: number | 'auto';
  breakpoints?: SwiperOptions['breakpoints'];
  loop?: boolean;
  autoplay?: boolean;
  /** Стрелки prev/next по бокам слайдера */
  navigation?: boolean;
  className?: string;
};

export const Slider = ({
  items,
  spaceBetween = 20,
  slidesPerView = 1,
  breakpoints,
  loop = false,
  autoplay = false,
  navigation = false,
  className = '',
}: SliderProps) => {
  const swiperRef = useRef<SwiperInstance | null>(null);
  const [navDisabled, setNavDisabled] = useState({ prev: true, next: false });

  const syncNav = useCallback(
    (swiper: SwiperInstance) => {
      if (!navigation) return;
      if (swiper.isLocked) {
        setNavDisabled({ prev: true, next: true });
        return;
      }
      if (loop) {
        setNavDisabled({ prev: false, next: false });
        return;
      }
      setNavDisabled({
        prev: swiper.isBeginning,
        next: swiper.isEnd,
      });
    },
    [navigation, loop]
  );

  return (
    <div className={clsx(styles.sliderWrapper, navigation && styles.sliderWrapperWithNav)}>
      {navigation && (
        <>
          <button
            type="button"
            className={clsx(styles.navBtn, styles.navPrev)}
            disabled={navDisabled.prev}
            aria-label={'Previous slide'}
            onClick={() => swiperRef.current?.slidePrev()}
          >
            <span aria-hidden>‹</span>
          </button>
          <button
            type="button"
            className={clsx(styles.navBtn, styles.navNext)}
            disabled={navDisabled.next}
            aria-label={'Next slide'}
            onClick={() => swiperRef.current?.slideNext()}
          >
            <span aria-hidden>›</span>
          </button>
        </>
      )}
      <Swiper
        className={clsx(styles.root, className)}
        modules={[Pagination, Autoplay]}
        pagination={{
          clickable: true,
        }}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          syncNav(swiper);
        }}
        onSlideChange={(swiper) => syncNav(swiper)}
        onBreakpoint={(swiper) => syncNav(swiper)}
        onResize={(swiper) => syncNav(swiper)}
        spaceBetween={spaceBetween}
        slidesPerView={slidesPerView}
        breakpoints={breakpoints}
        loop={loop}
        {...(autoplay && {
          autoplay: {
            delay: 5000,
            pauseOnMouseEnter: true,
            disableOnInteraction: false,
          },
        })}
      >
        {items.map((item, index) => (
          <SwiperSlide key={item.id ?? index}>{item.content}</SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};
